import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { DataTableMultiSelectFilterComponent, DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import {
  MinistryAppointmentManagementService,
  MinistryAppointmentDto,
  UpdateMinistryAppointmentDto,
} from '../../services/ministry-appointment-management.service';

@Component({
  selector: 'app-ministry-appointment-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PageHeaderComponent,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
  ],
  templateUrl: './ministry-appointment-list.component.html',
  styleUrl: './ministry-appointment-list.component.scss',
})
export class MinistryAppointmentListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true, editable: true, editType: 'text' },
    { key: 'isActive', header: 'Activo', hideable: true, visible: true, resizable: true },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false },
  ];

  tableData: MinistryAppointmentDto[] = [];
  isLoading = false;
  enableDragDrop = true;
  savingCell?: { row: MinistryAppointmentDto; column: string };
  activeFilterChips: FilterChip[] = [];
  allAppointments: MinistryAppointmentDto[] = [];
  filterForm: FormGroup;
  readonly statusFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private service: MinistryAppointmentManagementService,
    private dialog: MatDialog,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      searchTerm: [''],
      status: [['active']],
    });
  }

  ngOnInit(): void {
    this.loadAppointments();

    this.filterForm.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) =>
          previous.searchTerm === current.searchTerm && previous.status.join(',') === current.status.join(',')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyFilter());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createNew(): void {
    this.router.navigate(['/configuration/ministry-appointments/new']);
  }

  editAppointment(appointment: MinistryAppointmentDto): void {
    this.router.navigate(['/configuration/ministry-appointments', appointment.id, 'edit']);
  }

  deleteAppointment(appointment: MinistryAppointmentDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar Encargo Ministerial',
        message: `Estas seguro de que deseas eliminar el encargo "${appointment.name}"?`,
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.confirmDelete(appointment.id!);
        }
      });
  }

  onToolbarSearch(term: string): void {
    this.filterForm.get('searchTerm')?.setValue(term);
  }

  onFilterRemove(key: string): void {
    if (key === 'status') {
      this.filterForm.get('status')?.setValue(this.statusFilterOptions.map(option => option.value), { emitEvent: false });
      this.applyFilter();
    }
  }

  onAppointmentReorder(event: any): void {
    const { data } = event;

    data.forEach((appointment: MinistryAppointmentDto, index: number) => {
      this.service.updateSortOrder(appointment.id!, index + 1)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (error: any) => {
            console.error('Error updating appointment sort order:', error);
            this.notificationService.error('Error al actualizar el orden');
            this.loadAppointments();
            this.cdr.markForCheck();
          },
        });
    });

    this.allAppointments = [...data];
    this.notificationService.success('Orden actualizado exitosamente');
  }

  onStatusChange(appointment: MinistryAppointmentDto, newStatus: boolean): void {
    const oldStatus = appointment.isActive;
    appointment.isActive = newStatus;
    this.cdr.markForCheck();

    const updateDto = new UpdateMinistryAppointmentDto({
      name: appointment.name,
      sortOrder: appointment.sortOrder,
      isActive: newStatus,
    });

    this.service.update(appointment.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success(newStatus ? 'Encargo activado exitosamente' : 'Encargo desactivado exitosamente');
          this.applyFilter();
        },
        error: (error: any) => {
          console.error('Error updating appointment status:', error);
          appointment.isActive = oldStatus;
          this.notificationService.error('Error al actualizar el estado del encargo');
          this.loadAppointments();
          this.cdr.markForCheck();
        },
      });
  }

  onCellEditSave(event: CellEditSaveEvent<MinistryAppointmentDto>): void {
    if (event.column !== 'name') {
      return;
    }

    const newName = String(event.newValue ?? '').trim();
    const oldName = String(event.oldValue ?? '');

    if (!newName) {
      this.notificationService.warning('El nombre del encargo no puede quedar vacio');
      return;
    }

    if (newName === oldName) {
      return;
    }

    const appointment = event.row;
    this.savingCell = { row: appointment, column: event.column };
    this.cdr.markForCheck();

    const updateDto = new UpdateMinistryAppointmentDto({
      name: newName,
      sortOrder: appointment.sortOrder,
      isActive: appointment.isActive,
    });

    this.service.update(appointment.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          appointment.name = newName;
          this.tableData = [...this.tableData];
          this.notificationService.success('Encargo actualizado exitosamente');
          this.savingCell = undefined;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating appointment:', error);
          appointment.name = oldName;
          this.notificationService.error('Error al actualizar el encargo');
          this.savingCell = undefined;
          this.loadAppointments();
          this.cdr.markForCheck();
        },
      });
  }

  private loadAppointments(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: appointments => {
          this.allAppointments = appointments;
          this.applyFilter();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading ministry appointments:', error);
          this.notificationService.error('Error al cargar los encargos ministeriales');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private applyFilter(): void {
    const statusFilter = (this.filterForm.get('status')?.value ?? []) as string[];
    const searchTerm = String(this.filterForm.get('searchTerm')?.value ?? '').trim().toLowerCase();

    let filteredData = this.allAppointments;

    if (statusFilter.length === 0) {
      filteredData = [];
    } else {
      filteredData = filteredData.filter(appointment => statusFilter.includes(appointment.isActive ? 'active' : 'inactive'));
    }

    if (searchTerm) {
      filteredData = filteredData.filter(appointment => (appointment.name ?? '').toLowerCase().includes(searchTerm));
    }

    this.tableData = [...filteredData];
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  private buildFilterChips(): void {
    const status = (this.filterForm.get('status')?.value ?? []) as string[];

    if (status.length === this.statusFilterOptions.length) {
      this.activeFilterChips = [];
      return;
    }

    const label = status.length === 0
      ? 'Estado: Ninguno'
      : `Estado: ${status.map(value => this.statusFilterOptions.find(option => option.value === value)?.label ?? value).join(', ')}`;
    this.activeFilterChips = [{ key: 'status', label, removable: true }];
  }

  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Encargo ministerial eliminado exitosamente');
          this.loadAppointments();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error deleting ministry appointment:', error);
          this.notificationService.error((error.error as any)?.message || 'Error al eliminar el encargo');
          this.cdr.markForCheck();
        },
      });
  }
}
